/// <amd-module name="Wit/WorkItemTFVCLinks/TFVCLinkUtils" />
import { getTFSProject } from "Tfs/Platform/Page";
import { TfvcChangeset, TfvcChangesetRef, TfvcItem } from "VC/Common/Contracts/Git";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLocationService } from "VSS/Platform/Location";
import { LinkTypeFieldIds, RegisteredLinkTypeNames, ToolIds } from "Wit/WorkItemLinksRegistration/Constants";
import { ILinkIdentifier, IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import { getLinkTypeDisplayName } from "Wit/WorkItemLinksRegistration/LinkUtils";
import * as Resources from "Wit/WorkItemTFVCLinks/Resources";

export const latestVersion = "T";
type LatestVersion = typeof latestVersion;
export type VersionLiteral = number | LatestVersion;

export interface IVersionedItemInfo {
    itemPath: string;
    changesetVersion: VersionLiteral;
    deletionId: number;
}

export function changesetToDisplayData(
    pageContext: IVssPageContext,
    changeset: TfvcChangeset | TfvcChangesetRef,
    linkIdentifier?: ILinkIdentifier,
    path?: string
): IResolvedLinkDisplayData {
    const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");
    linkIdentifier = linkIdentifier ?? buildLinkIdentifierForNewChangesetLink(changeset);
    const projectName = extractChangesetProject(path) ?? getTFSProject(pageContext)?.name;
    const changesetURL = changeset && changeset._links && changeset._links.web && changeset._links.web.href;
    return {
        tool: ToolIds.VersionControl,
        id: changeset.changesetId,
        displayId: format(Resources.ChangesetWithId, changeset.changesetId),
        title: changeset.comment,
        updateDate: changeset.createdDate,
        icons: [{ iconName: "TFVCLogo" }, changeset.checkedInBy],
        registeredLinkType: RegisteredLinkTypeNames.Changeset,
        linkTypeDisplayName: getLinkTypeDisplayName(RegisteredLinkTypeNames.Changeset),
        uri:
            changesetURL ??
            locationService.routeUrl("ms.vss-code-web.tfvc-changeset-details-route", {
                parameters: changeset.changesetId.toString(),
                project: projectName || ""
            }),
        linkIdentifier,
        additionalData: { changeset }
    };
}

/**
 * e.g. vstfs:///VersionControl/Changeset/3", => 3
 */
export function extractChangesetId(filePath: string): number | undefined {
    const suffix = filePath.substr(filePath.lastIndexOf("/") + 1);
    const asInt = parseInt(suffix);
    return Number.isFinite(asInt) ? asInt : undefined;
}

export function buildLinkIdentifierForNewChangesetLink(changeset: TfvcChangeset | TfvcChangesetRef): ILinkIdentifier {
    return {
        tool: ToolIds.VersionControl,
        FldID: LinkTypeFieldIds.ExternalLink,
        FilePath: `vstfs:///VersionControl/Changeset/${changeset.changesetId}`,
        OriginalName: RegisteredLinkTypeNames.Changeset
    };
}

export function versionedItemToDisplayData(
    pageContext: IVssPageContext,
    versionedItem: TfvcItem,
    versionLiteral: VersionLiteral,
    linkIdentifier?: ILinkIdentifier
): IResolvedLinkDisplayData {
    const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");
    linkIdentifier = linkIdentifier ?? buildLinkIdentifierForNewVersionedItemLink(versionedItem, versionLiteral);
    return {
        tool: ToolIds.VersionControl,
        id: versionedItem.version,
        displayId: "" + versionedItem.version,
        title:
            versionLiteral === "T"
                ? format(Resources.LatestVersionOfItem, versionedItem.path)
                : format(Resources.VersionNOfItem, versionedItem.version, versionedItem.path),
        updateDate: versionedItem.changeDate,
        icons: [{ iconName: "TextDocument" }],
        registeredLinkType: RegisteredLinkTypeNames.VersionedItem,
        linkTypeDisplayName: getLinkTypeDisplayName(RegisteredLinkTypeNames.VersionedItem),
        uri: locationService.routeUrl("ms.vss-code-web.tfvc-files-route", {
            project: separateRoot(versionedItem.path)[0]!.substr(2),
            path: versionedItem.path,
            version: versionLiteral.toString(),
            _a: "comments"
        }),
        linkIdentifier,
        additionalData: { versionedItem }
    };
}

/**
 * The path of the versioned item is encoded three times, which is bananas.
 *
 * We start with something like:
 * vstfs:///VersionControl/VersionedItem/%252524%25252Ftest-tfvc%25252Ftest.txt%2526changesetVersion%253DT%2526deletionId%253D0
 *
 * Take off the last part:
 * %252524%25252Ftest-tfvc%25252Ftest.txt%2526changesetVersion%253DT%2526deletionId%253D0
 *
 * decodeURIComponent:
 * %2524%252Ftest-tfvc%252Ftest.txt%26changesetVersion%3DT%26deletionId%3D0
 *
 * decodeURIComponent:
 * %24%2Ftest-tfvc%2Ftest.txt&changesetVersion=T&deletionId=0
 *
 * decodeURIComponent:
 * $/test-tfvc/test.txt&changesetVersion=T&deletionId=0
 */
export function extractVersionedItemInfo(filePath: string): IVersionedItemInfo | undefined {
    const suffix = filePath.substr(filePath.lastIndexOf("/") + 1);
    const decoded = decodeURIComponent(decodeURIComponent(decodeURIComponent(suffix)));
    const pieces = decoded.split("&");

    let itemPath: string | undefined = undefined;
    let changesetVersion: VersionLiteral = latestVersion;
    let deletionId: number = 0;

    for (const piece of pieces) {
        if (piece.startsWith("$/")) {
            itemPath = piece;
            continue;
        }

        if (piece.startsWith("changesetVersion=")) {
            changesetVersion = parseInt(piece.substr("changesetVersion=".length));
            changesetVersion = Number.isFinite(changesetVersion) ? changesetVersion : latestVersion;
            continue;
        }

        if (piece.startsWith("deletionId=")) {
            deletionId = parseInt(piece.substr("deletionId=".length));
            deletionId = Number.isFinite(deletionId) ? deletionId : 0;
        }
    }

    if (!itemPath) {
        return undefined;
    }

    return { itemPath, changesetVersion, deletionId };
}

function extractChangesetProject(path: string | undefined): string | undefined {
    if (path && !path.startsWith("$/")) {
        return undefined;
    }
    return path ? path.split("/")[1] : undefined;
}

export function buildLinkIdentifierForNewVersionedItemLink(versionedItem: TfvcItem, versionLiteral: VersionLiteral): ILinkIdentifier {
    const suffix = `${versionedItem.path}&changesetVersion=${versionLiteral}&deletionId=0`;

    // As I mentioned above, this is bananas. But this is how it's currently done, so that's what we have to do.
    const encodedSuffix = encodeURIComponent(encodeURIComponent(encodeURIComponent(suffix)));

    return {
        tool: ToolIds.VersionControl,
        FldID: LinkTypeFieldIds.ExternalLink,
        FilePath: `vstfs:///VersionControl/VersionedItem/${encodedSuffix}`,
        OriginalName: RegisteredLinkTypeNames.VersionedItem
    };
}

export function separateRoot(path: string): [string | undefined, string | undefined] {
    if (!path.startsWith("$/")) {
        return [undefined, undefined];
    }

    const [_dollarSign, project, ...rest] = path.split("/");
    return [`$/${project}`, rest.join("/")];
}